import {
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { DateRangePicker } from "@mui/x-date-pickers-pro";
import { useMemo } from "react";
import { Controller, FieldValues, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import InfoTooltip from "../../../common/components/functional/InfoTooltip/InfoTooltip";
import { CustomerContractAutocomplete } from "../../../common/components/functional/customerContractAutocomplete/CustomerContractAutocomplete";
import { SpinnerButton } from "../../../common/components/functional/spinnerButton/SpinnerButton";
import {
  SelectLight,
  TextFieldLight,
} from "../../../common/components/styled/styled";
import { AutocompleteType } from "../../../common/types/autoComplete";
import { INVOICE_WAVE_ONE, INVOICE_WAVE_TWO } from "../../invoices/constants";
import { CANCEL_ASSIGNMENT_OPTIONS } from "../../orders/constants";
import InputRow from "../InputRow";
import { SCHEDULING_PERIOD_TYPES } from "../helpers/utils";

type Props = {
  action: string;
  onSubmit: (fieldValues: FieldValues) => void;
  isLoading: boolean;
};

export const BackgroundJobForm = ({ action, onSubmit, isLoading }: Props) => {
  const { t } = useTranslation();
  const { control, getValues } = useFormContext();

  const checkIfAbleToSave = () => {
    return true;
  };

  const cancelAssignmentOptions = useMemo(() => {
    return CANCEL_ASSIGNMENT_OPTIONS;
  }, []);

  return (
    <>
      {action !== "recalculateCustomerPricing" &&
      action !== "createPayerSummaryReportDelivery" &&
      action !== "createWageReportDelivery" &&
      action !== "deleteWageReportDelivery" &&
      action !== "createTaxPayment" &&
      action !== "exportTaxPayment" &&
      action !== "createTradeUnionPayment" &&
      action !== "exportTradeUnionPayment" &&
      action !== "createHolidayBacklogReports" &&
      action !== "updateWageReportDeliveryStatus" &&
      action !== "updatePayerSummaryReportDeliveryStatus" &&
      action !== "acceptWorkAgreement" &&
      action !== "cancelAssignment" &&
      action !== "updateInvoice" &&
      action !== "deleteInvoice" &&
      action !== "rebuildWorkHistoryTimes" &&
      action !== "redactTalentData" &&
      action !== "acceptWorkOffer" &&
      action !== "processWorkOffers" ? (
        <InputRow
          label={t("settings:schedulingClassType")}
          field={
            <Controller
              control={control}
              name="schedulingClass"
              render={({ field: { value, onChange } }) => (
                <FormControl fullWidth>
                  <SelectLight
                    sx={{ maxWidth: "450px" }}
                    size="small"
                    value={value}
                    onChange={onChange}
                  >
                    {SCHEDULING_PERIOD_TYPES.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {t(`schedulingPeriods:${type.name}`)}
                      </MenuItem>
                    ))}
                  </SelectLight>
                </FormControl>
              )}
            />
          }
        />
      ) : null}
      {action === "createSalaryPayment" ||
      action === "createTaxPayment" ||
      action === "exportTaxPayment" ||
      action === "createGarnishmentPayment" ||
      action === "exportGarnishmentPayment" ||
      action === "createTradeUnionPayment" ||
      action === "exportTradeUnionPayment" ||
      action === "createPayerSummaryReportDelivery" ? (
        <InputRow
          label={t("settings:eezyCompanyId")}
          field={
            <Controller
              control={control}
              name="eezyCompanyId"
              render={({ field: { value, onChange } }) => (
                <TextFieldLight
                  sx={{ maxWidth: "450px" }}
                  size="small"
                  fullWidth
                  value={value}
                  onChange={onChange}
                />
              )}
            />
          }
        />
      ) : null}
      {action !== "autoApproveEntries" &&
      action !== "createWageReportDelivery" &&
      action !== "deleteWageReportDelivery" &&
      action !== "updateWageReportDeliveryStatus" &&
      action !== "updatePayerSummaryReportDeliveryStatus" &&
      action !== "acceptWorkAgreement" &&
      action !== "cancelAssignment" &&
      action !== "updateInvoice" &&
      action !== "acceptWorkOffer" &&
      action !== "processWorkOffers" &&
      action !== "rebuildWorkHistoryTimes" &&
      action !== "redactTalentData" &&
      action !== "deleteInvoice" ? (
        <InputRow
          label={t("date")}
          field={
            <Controller
              control={control}
              name="date"
              render={({ field }) => (
                <DesktopDatePicker
                  sx={{ maxWidth: "450px" }}
                  {...field}
                  value={field.value}
                  format="dd.MM.yyyy"
                  onChange={(newDate: Date | null) => field.onChange(newDate)}
                  slots={{ textField: TextFieldLight }}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                    },
                  }}
                />
              )}
            />
          }
        />
      ) : null}
      {action === "sendInvoices" || action === "lockInvoices" ? (
        <InputRow
          label={t("invoices:invoiceWaves")}
          field={
            <Controller
              control={control}
              name="wave"
              render={({ field: { value, onChange } }) => (
                <FormControl fullWidth>
                  <SelectLight
                    sx={{ maxWidth: "450px" }}
                    size="small"
                    value={value}
                    onChange={onChange}
                  >
                    {[INVOICE_WAVE_ONE, INVOICE_WAVE_TWO].map((type) => (
                      <MenuItem key={type} value={type}>
                        {t(`invoices:${type}`)}
                      </MenuItem>
                    ))}
                  </SelectLight>
                </FormControl>
              )}
            />
          }
        />
      ) : null}
      {action === "recalculateCustomerPricing" ? (
        <InputRow
          label={t("invoices:contract")}
          field={
            <Controller
              control={control}
              name="contractId"
              render={({ field: { value, onChange } }) => (
                <FormControl sx={{ maxWidth: "450px" }} fullWidth>
                  <CustomerContractAutocomplete
                    name="contract"
                    defaultValue={value}
                    onChange={(_: any, newValue: AutocompleteType[]) =>
                      onChange(newValue)
                    }
                  />
                </FormControl>
              )}
            />
          }
        />
      ) : null}
      {action === "autoApproveEntries" ? (
        <InputRow
          label={t("settings:dateRange")}
          field={
            <Controller
              control={control}
              name="range"
              render={({ field: { value, onChange } }) => (
                <DateRangePicker
                  sx={{ maxWidth: "450px" }}
                  format="dd.MM.yyyy"
                  value={value}
                  onChange={onChange}
                  slotProps={{
                    textField: { size: "small", label: null },
                  }}
                />
              )}
            />
          }
        />
      ) : null}
      {action === "rebuildWorkHistoryTimes" ? (
        <>
          <InputRow
            label={t("settings:dateRange")}
            field={
              <Controller
                control={control}
                name="range"
                render={({ field: { value, onChange } }) => (
                  <DateRangePicker
                    sx={{ maxWidth: "450px" }}
                    format="dd.MM.yyyy"
                    value={value}
                    onChange={onChange}
                    slotProps={{
                      textField: { size: "small", label: null },
                    }}
                  />
                )}
              />
            }
          />
          <InputRow
            label={t("settings:talentId")}
            field={
              <Controller
                control={control}
                name="talentId"
                render={({ field: { value, onChange } }) => (
                  <TextFieldLight
                    sx={{ maxWidth: "450px" }}
                    size="small"
                    fullWidth
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            }
          />
        </>
      ) : null}
      {action === "createWageReportDelivery" ? (
        <InputRow
          label={t("settings:payslipId")}
          field={
            <Controller
              control={control}
              name="payslipId"
              render={({ field: { value, onChange } }) => (
                <TextFieldLight
                  sx={{ maxWidth: "450px" }}
                  size="small"
                  fullWidth
                  value={value}
                  onChange={onChange}
                />
              )}
            />
          }
        />
      ) : null}
      {action === "createSalaryPayment" ? (
        <Grid item container xs={12}>
          <Grid item xs={4} container alignItems={"center"}>
            <Typography>{t("settings:numberOfPayslips")}</Typography>
            {action === "createSalaryPayment" ? (
              <InfoTooltip
                title={t(`settings:numberOfPayslipsInfo`)}
                style={{ marginLeft: "5px" }}
              />
            ) : null}
          </Grid>
          <Grid item xs={6}>
            <Controller
              control={control}
              name="numberOfPayslips"
              render={({ field: { value, onChange } }) => (
                <TextFieldLight
                  size="small"
                  fullWidth
                  value={value}
                  onChange={onChange}
                />
              )}
            />
          </Grid>
        </Grid>
      ) : null}
      {action === "updateWageReportDeliveryStatus" ? (
        <InputRow
          label={t("settings:wageReportDeliveryId")}
          field={
            <Controller
              control={control}
              name="wageReportDeliveryId"
              render={({ field: { value, onChange } }) => (
                <TextFieldLight
                  sx={{ maxWidth: "450px" }}
                  size="small"
                  fullWidth
                  value={value}
                  onChange={onChange}
                />
              )}
            />
          }
        />
      ) : null}
      {action === "updatePayerSummaryReportDeliveryStatus" ? (
        <InputRow
          label={t("settings:payerSummaryReportDeliveryId")}
          field={
            <Controller
              control={control}
              name="payerSummaryReportDeliveryId"
              render={({ field: { value, onChange } }) => (
                <TextFieldLight
                  sx={{ maxWidth: "450px" }}
                  size="small"
                  fullWidth
                  value={value}
                  onChange={onChange}
                />
              )}
            />
          }
        />
      ) : null}
      {action === "deleteWageReportDelivery" ? (
        <InputRow
          label={t("settings:wageReportDeliveryId")}
          field={
            <Controller
              control={control}
              name="wageReportDeliveryId"
              render={({ field: { value, onChange } }) => (
                <TextFieldLight
                  sx={{ maxWidth: "450px" }}
                  size="small"
                  fullWidth
                  value={value}
                  onChange={onChange}
                />
              )}
            />
          }
        />
      ) : null}
      {action === "acceptWorkAgreement" ? (
        <>
          <InputRow
            label={t("settings:workAgreementId")}
            field={
              <Controller
                control={control}
                name="workAgreementId"
                render={({ field: { value, onChange } }) => (
                  <TextFieldLight
                    sx={{ maxWidth: "450px" }}
                    size="small"
                    fullWidth
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            }
          />
          <InputRow
            label={t("settings:version")}
            field={
              <Controller
                control={control}
                name="version"
                render={({ field: { value, onChange } }) => (
                  <TextFieldLight
                    sx={{ maxWidth: "100px" }}
                    size="small"
                    fullWidth
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            }
          />
        </>
      ) : null}
      {action === "acceptWorkOffer" ? (
        <>
          <InputRow
            label={t("settings:orderId")}
            field={
              <Controller
                control={control}
                name="bundleId"
                render={({ field: { value, onChange } }) => (
                  <TextFieldLight
                    sx={{ maxWidth: "450px" }}
                    size="small"
                    fullWidth
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            }
          />
          <InputRow
            label={t("settings:talentId")}
            field={
              <Controller
                control={control}
                name="talentId"
                render={({ field: { value, onChange } }) => (
                  <TextFieldLight
                    sx={{ maxWidth: "450px" }}
                    size="small"
                    fullWidth
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            }
          />
          <InputRow
            label={t("settings:startTime")}
            field={
              <Controller
                control={control}
                name="startingShiftAt"
                render={({ field: { value, onChange } }) => (
                  <TextFieldLight
                    sx={{ maxWidth: "450px" }}
                    size="small"
                    fullWidth
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            }
          />
        </>
      ) : null}
      {action === "processWorkOffers" ? (
        <InputRow
          label={t("settings:orderId")}
          field={
            <Controller
              control={control}
              name="workOffersId"
              render={({ field: { value, onChange } }) => (
                <TextFieldLight
                  sx={{ maxWidth: "450px" }}
                  size="small"
                  fullWidth
                  value={value}
                  onChange={onChange}
                />
              )}
            />
          }
        />
      ) : null}
      {action === "cancelAssignment" ? (
        <>
          <InputRow
            label={t("settings:assignmentId")}
            field={
              <Controller
                control={control}
                name="assignmentId"
                render={({ field: { value, onChange } }) => (
                  <TextFieldLight
                    sx={{ maxWidth: "450px" }}
                    size="small"
                    fullWidth
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            }
          />
          <InputRow
            label={t("settings:paySalary")}
            field={
              <Controller
                control={control}
                name="paySalary"
                render={({ field: { value, onChange } }) => (
                  <RadioGroup
                    value={value}
                    onChange={(_, value) => {
                      const booleanValue: boolean = value === "true";
                      onChange(booleanValue);
                    }}
                  >
                    <FormControlLabel
                      label={t("common:yes")}
                      value={true}
                      control={<Radio />}
                    />
                    <FormControlLabel
                      label={t("common:no")}
                      value={false}
                      control={<Radio />}
                    />
                  </RadioGroup>
                )}
              />
            }
          />
          <InputRow
            label={t("settings:reason")}
            field={
              <Controller
                control={control}
                name="reason"
                render={({ field: { value, onChange } }) => (
                  <SelectLight
                    sx={{ width: "450px", maxWidth: "450px" }}
                    size="small"
                    value={value}
                    onChange={onChange}
                  >
                    {cancelAssignmentOptions.map((option) => (
                      <MenuItem key={option.id} value={option.value}>
                        {t(`order:${option.value}`)}
                      </MenuItem>
                    ))}
                  </SelectLight>
                )}
              />
            }
          />
        </>
      ) : null}
      {action === "updateInvoice" || action === "deleteInvoice" ? (
        <InputRow
          label={t("settings:invoiceId")}
          field={
            <Controller
              control={control}
              name="invoiceId"
              render={({ field: { value, onChange } }) => (
                <TextFieldLight
                  sx={{ maxWidth: "450px" }}
                  size="small"
                  fullWidth
                  value={value}
                  onChange={onChange}
                />
              )}
            />
          }
        />
      ) : null}
      {action === "redactTalentData" ? (
        <>
          <InputRow
            label={t("settings:talentId")}
            field={
              <Controller
                control={control}
                name="talentId"
                render={({ field: { value, onChange } }) => (
                  <TextFieldLight
                    sx={{ maxWidth: "450px" }}
                    size="small"
                    fullWidth
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            }
          />
        </>
      ) : null}

      <Grid item container>
        <Grid item xs={12}>
          <SpinnerButton
            label={t("save")}
            onClick={() => onSubmit(getValues())}
            disabled={!checkIfAbleToSave()}
            variant="contained"
            isLoading={isLoading}
            color="primary"
            sx={{
              height: "36px",
              borderRadius: "20px",
            }}
          />
        </Grid>
      </Grid>
    </>
  );
};
